<script setup>
import { Badge } from '@dashhudson/dashing-ui/badge';
import { ChannelSelectList } from '@dashhudson/dashing-ui/channel-dropdown';
import { DateRangePicker } from '@dashhudson/dashing-ui/date-range-picker';
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { FilterPanel } from '@dashhudson/dashing-ui/filter-panel';
import {
  FilterSidePanelContent,
  useFilterSidePanel,
} from '@dashhudson/dashing-ui/filter-side-panel';
import { InputText } from '@dashhudson/dashing-ui/input-text';
import { LimitedSelectList } from '@dashhudson/dashing-ui/limited-select-list';
import { SelectList } from '@dashhudson/dashing-ui/select-list';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import mapValues from 'lodash/mapValues';
import omitBy from 'lodash/omitBy';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import LocationFilterPanel from '@/app/socialListening/components/shared/LocationFilterPanel.vue';
import { useFilterPanelManager } from '@/app/socialListening/composables/useFilterPanelManager';
import { useListeningPermissions } from '@/app/socialListening/composables/useListeningPermissions';
import { useLocationOptions } from '@/app/socialListening/composables/useLocationOptions';
import {
  LISTENING_FILTER_TOOLTIPS,
  LISTENING_SCOPED_FILTERS,
  TOPIC_DATE_FILTER_OPTIONS,
} from '@/app/socialListening/constants/listeningFilters';
import {
  convertToScopedTopicFilters,
  convertToSearchBodyFilters,
  formatDateRange,
} from '@/app/socialListening/utils/filter-utils';
import { useSocialListeningStore } from '@/stores/social-listening';
import { useSocialListeningComparisonsStore } from '@/stores/social-listening-comparisons';
import { useSocialListeningTopPostsStore } from '@/stores/social-listening-top-posts';
import { useSocialListeningTopicsStore } from '@/stores/social-listening-topics';
import { lastDateRange } from '@/utils/dateUtils';

const route = useRoute();

const listeningStore = useSocialListeningStore();
const comparisonStore = useSocialListeningComparisonsStore();
const topicsStore = useSocialListeningTopicsStore();
const topPostsStore = useSocialListeningTopPostsStore();

const {
  scopedFilters: appliedFilters,
  internalFilters,
  internalUnappliedFilters,
  apply,
  reset,
} = useFilterSidePanel();

const { currentDrawer } = useDrawer();

const locationFilterPanelRef = ref(null);
const { lookupLocationsInOptions } = useLocationOptions();
const { commentsEnabled } = useListeningPermissions();

const FILTER_MAP = computed(() => {
  const comparisonFilters = LISTENING_SCOPED_FILTERS.COMPARISON;

  if (!commentsEnabled.value) {
    const mentionTypes = comparisonFilters.options.mentionTypes.filter(
      (item) => !['COMMENT', 'UGC'].includes(item.value),
    );
    return {
      ...comparisonFilters,
      options: {
        ...comparisonFilters.options,
        mentionTypes,
      },
    };
  }

  return comparisonFilters;
});

const filterManager = useFilterPanelManager({ filterMap: FILTER_MAP });
const {
  selectedKeywordFilter,
  isTopPostsDrawerOpen,
  industryOptions: baseIndustryOptions,
  detectFilterChanges,
  isDateRangeValid,
  buildFilterOverrides,
  getFiltersFromStorage,
  setFiltersToStorage,
} = filterManager;

const dateRangePresets = computed(() =>
  Object.values(TOPIC_DATE_FILTER_OPTIONS).map((option) => ({
    ...option,
    range: () => lastDateRange(option.subtractValue, option.subtractUnit),
  })),
);

const topicOptions = computed(() =>
  Object.values(comparisonStore.selectedComparison?.topics ?? []).map((topic) => ({
    label: topic.name,
    value: topic.id,
  })),
);

const industryOptions = baseIndustryOptions;

// Current filter data from the store (excludes keywords/websearch, includes topicIds)
const currentFilterData = computed(() => {
  const { keywordsAndHashtags, websearch, ...filterData } = comparisonStore.searchFilters || {};
  const topicIds = comparisonStore.filteredTopics?.map((topic) => topic.id);
  const filters = { topicIds, ...filterData };
  return filters || {};
});

// Filters formatted for display in the UI (with date range formatted)
const formattedFiltersForUI = computed(() => {
  const { sourceCreated, ...filterData } = currentFilterData.value || {};
  if (isEmpty(currentFilterData.value) && !sourceCreated) {
    return {};
  }
  const formattedSourceCreated = formatDateRange(
    sourceCreated?.onOrAfter,
    sourceCreated?.onOrBefore,
    sourceCreated?.rollingDateRangeOffset,
  );
  const defaultFilters = mapValues(
    { ...LISTENING_SCOPED_FILTERS.COMPARISON.default },
    () => undefined,
  );
  return {
    ...defaultFilters,
    ...omitBy(
      { ...filterData, sourceCreated: formattedSourceCreated },
      (v) => isEmpty(v) && !isNumber(v),
    ),
  };
});

// Convert store filters to scoped format for the filter panel (handles sourceCreated conversion)
const storeFiltersInScopedFormat = computed(() => {
  if (!isEmpty(currentFilterData.value)) {
    const filters = {
      topicIds: currentFilterData.value.topicIds,
      ...convertToScopedTopicFilters(currentFilterData.value),
    };
    if (filters?.locations?.length) {
      filters.locations = lookupLocationsInOptions(filters.locations);
    }
    return filters;
  }
  return null;
});

// Filters that have been applied by the user (converted to search body format)
const appliedSearchFilters = computed(() => ({
  topicIds: appliedFilters?.value.topicIds,
  ...convertToSearchBodyFilters(appliedFilters.value),
}));

const selectedComparisonId = computed(() => (route.params?.id ? Number(route.params.id) : null));

// Apply filters - either to drawer (temporary) or to the whole page
function applyFilters(newFilters) {
  internalFilters[LISTENING_SCOPED_FILTERS.COMPARISON.scope] = { ...newFilters };

  // When drawer is open, only update temp filters for drawer - don't apply to whole page
  if (isTopPostsDrawerOpen.value) {
    apply(); // This syncs internalFilters -> appliedFilters in the filter side panel
    const filtersToApply = convertToSearchBodyFilters(appliedFilters.value);
    comparisonStore.setTempFilters(filtersToApply);
  } else {
    // When drawer is closed, apply to whole page as normal
    apply();
  }

  locationFilterPanelRef.value?.resetKey();
}

// Update filters in the store (no-op if drawer is open)
function updateFilters(filtersObj) {
  if (isTopPostsDrawerOpen.value) {
    locationFilterPanelRef.value?.resetKey();
    return;
  }
  comparisonStore.setComparisonFilters(filtersObj);
  locationFilterPanelRef.value?.resetKey();
}

async function refreshComparisonPageData() {
  await comparisonStore.initLoadComparisonData(selectedComparisonId.value);
  await topicsStore.getSelectedTopicData();
  topicsStore.filters = {
    keywordsAndHashtags: topicsStore.filters.keywordsAndHashtags,
    ...comparisonStore.comparisonFilters,
  };
  await topicsStore.getTopPostsPreview();
  await topicsStore.getTopLocations();
}

onBeforeMount(() => {
  const storedFilters = getFiltersFromStorage('comparison', selectedComparisonId.value);
  if (isEmpty(storedFilters)) return;
  updateFilters(storedFilters);
});

// Watch for comparison changes and restore stored filters or apply current filters
watch(
  () => comparisonStore.selectedComparison?.id,
  async (newId, oldId) => {
    if (!newId || isTopPostsDrawerOpen.value) return;

    if (oldId) reset();

    const filterData = appliedSearchFilters.value || {};
    const filterOverrides = omitBy(filterData, isEmpty);

    const storedFilters = getFiltersFromStorage('comparison', selectedComparisonId.value);
    if (!isEmpty(storedFilters)) {
      updateFilters(storedFilters);
      return;
    }
    updateFilters(filterOverrides);
  },
  { immediate: true },
);

// Sync store filter changes to filter panel (critical for drawer functionality)
watch(
  () => currentFilterData.value,
  (storeFilters) => {
    // Only sync when filters differ and drawer is closed (prevent circular updates)
    if (
      !isEqual(storeFilters, internalFilters[LISTENING_SCOPED_FILTERS.COMPARISON.scope]) &&
      !isTopPostsDrawerOpen.value
    ) {
      internalFilters[LISTENING_SCOPED_FILTERS.COMPARISON.scope] = { ...storeFilters };
      applyFilters(storeFiltersInScopedFormat.value);
    }
  },
  { deep: true },
);

// Use helper functions from filter manager
const hasFiltersChanged = detectFilterChanges;

// Helper: Check if store should be updated based on all conditions
function shouldPersistFiltersToStore(currentFilters, previousFilters, filterOverrides) {
  if (!previousFilters || isTopPostsDrawerOpen.value) return false;

  const dateRangeValid = isDateRangeValid(currentFilters?.sourceCreated);
  const hasChanged = hasFiltersChanged(currentFilters, previousFilters);
  const savedFilterData = omitBy(formattedFiltersForUI.value, isEmpty);

  return (
    dateRangeValid &&
    hasChanged &&
    !isEmpty(formattedFiltersForUI.value) &&
    !isEqual(filterOverrides, savedFilterData) &&
    isEmpty(internalUnappliedFilters.value)
  );
}

// Helper: Persist filters to store and refresh page data
async function persistFiltersAndRefresh(filterOverrides) {
  updateFilters(filterOverrides);
  setFiltersToStorage('comparison', selectedComparisonId.value, filterOverrides);
  await refreshComparisonPageData();
}

// Helper: Prepare filters for the drawer
function prepareDrawerFilters(filterOverrides) {
  const defaultFilters = LISTENING_SCOPED_FILTERS.TOPIC.default;
  const { topicIds, ...filterData } = filterOverrides;
  return {
    ...defaultFilters,
    keywordsAndHashtags: comparisonStore.drawerSelectedTopic.searchBody.filters.keywordsAndHashtags,
    ...filterData,
  };
}

// Watch for filter changes and update the store/refresh data accordingly
watch(
  () => appliedSearchFilters.value,
  async (currentFilters, previousFilters) => {
    const filterOverrides = buildFilterOverrides(currentFilters);

    // Update store and refresh page data if all conditions are met
    if (shouldPersistFiltersToStore(currentFilters, previousFilters, filterOverrides)) {
      await persistFiltersAndRefresh(filterOverrides);
    }

    // Handle drawer-specific filter updates (temporary filters)
    if (isTopPostsDrawerOpen.value) {
      const drawerFilters = prepareDrawerFilters(filterOverrides);
      comparisonStore.setTempFilters(drawerFilters);
    }
  },
  { deep: true },
);
</script>

<template>
  <FilterSidePanelContent
    v-slot="{ scopedInternalFilters }"
    :scope="LISTENING_SCOPED_FILTERS.COMPARISON.scope"
    :initial-state="LISTENING_SCOPED_FILTERS.COMPARISON.default"
    commit-on-apply
  >
    <!-- Keyword Filter -->
    <template v-if="selectedKeywordFilter?.keyword && scopedInternalFilters.keyword">
      <FilterPanel
        :title="selectedKeywordFilter.title"
        filters="keyword"
        no-remember
        data-test="ComparisonKeywordFilter"
      >
        <InputText v-model="scopedInternalFilters.keyword" disabled />
      </FilterPanel>
    </template>
    <!-- Channels Filter -->
    <FilterPanel
      title="Channels"
      filters="sources"
      no-remember
      data-test="ComparisonChannelsFilter"
    >
      <ChannelSelectList
        v-model="scopedInternalFilters.sources"
        :channels="LISTENING_SCOPED_FILTERS.COMPARISON.options.sources"
      />
    </FilterPanel>
    <!-- Reporting Period Filter -->
    <FilterPanel
      title="Reporting Period"
      filters="sourceCreated"
      no-remember
      :tooltip="LISTENING_FILTER_TOOLTIPS.REPORTING_PERIOD"
      data-test="ComparisonReportingPeriodFilter"
    >
      <DateRangePicker
        v-model="scopedInternalFilters.sourceCreated"
        :presets="dateRangePresets"
        :max-date="new Date()"
      />
    </FilterPanel>
    <!-- Topic Filter -->
    <FilterPanel
      v-if="topicOptions.length && !isTopPostsDrawerOpen"
      title="Topic"
      filters="topicIds"
      no-remember
      data-test="ComparisonTopicFilter"
    >
      <LimitedSelectList
        v-model="scopedInternalFilters.topicIds"
        :options="topicOptions"
        :min-options="2"
        multiple
      />
    </FilterPanel>
    <!-- Sentiment Filter -->
    <FilterPanel
      title="Sentiment"
      filters="sentiment"
      no-remember
      data-test="ComparisonSentimentFilter"
    >
      <SelectList
        v-model="scopedInternalFilters.sentiment"
        :options="LISTENING_SCOPED_FILTERS.COMPARISON.options.sentiment"
        multiple
      >
        <template #optionLabel="{ option, label }">
          <div class="dash-flex dash-gap-2 dash-items-center">
            <Badge dot :severity="option.severity" />
            <span>{{ label }}</span>
          </div>
        </template>
      </SelectList>
    </FilterPanel>
    <!-- Media Type Filter -->
    <FilterPanel
      title="Media Type"
      filters="mediaTypes"
      no-remember
      data-test="ComparisonMediaTypeFilter"
    >
      <SelectList
        v-model="scopedInternalFilters.mediaTypes"
        :options="LISTENING_SCOPED_FILTERS.COMPARISON.options.mediaTypes"
        multiple
      />
    </FilterPanel>
    <!-- Mention Type Filter -->
    <FilterPanel
      title="Mention Type"
      filters="mentionTypes"
      no-remember
      data-test="ComparisonMentionTypeFilter"
    >
      <LimitedSelectList
        v-model="scopedInternalFilters.mentionTypes"
        :options="FILTER_MAP.options.mentionTypes"
        multiple
      />
    </FilterPanel>
    <!-- Location Filter -->
    <LocationFilterPanel
      ref="locationFilterPanelRef"
      v-model="scopedInternalFilters.locations"
      data-test="ComparisonLocationFilter"
    />
    <!-- Industry Filter -->
    <FilterPanel
      v-if="industryOptions.length"
      title="Industry"
      filters="industryIds"
      no-remember
      data-test="ComparisonIndustryFilter"
    >
      <LimitedSelectList
        v-model="scopedInternalFilters.industryIds"
        :options="industryOptions"
        multiple
        searchable
      />
    </FilterPanel>
  </FilterSidePanelContent>
</template>
